import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import { Modal, Button } from 'react-bootstrap';
import axios from '../../axios/axiosconf';
import { ordenarListaAlfabetica } from './superadminSortUtils';
import './SuperadminStyles.css';

const formatBytes = (bytes) => {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const ArchivosClinicosControl = () => {
    const history = useHistory();
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [editLimiteGb, setEditLimiteGb] = useState(0);
    const [editError, setEditError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');
        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchClientes(1, '');
    }, [history]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        setCurrentPage(1);
        fetchClientes(1, debouncedSearch);
    }, [debouncedSearch]);

    const fetchClientes = async (page, search) => {
        try {
            setLoading(true);
            const response = await axios.get(`/superadmin/archivos-clinicos?page=${page}&search=${encodeURIComponent(search)}`);
            setClientes(ordenarListaAlfabetica(response.data.data || [], 'nombre'));
            setCurrentPage(response.data.current_page || 1);
            setTotalPages(response.data.last_page || 1);
        } catch (error) {
            console.error('Error fetching archivos clínicos:', error);
            if (error.response?.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
            }
        } finally {
            setLoading(false);
        }
    };

    const showAlertMessage = (type, message) => {
        setAlert({ show: true, type, message });
        setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
    };

    const getRowClass = (cliente) => {
        if (!cliente.modulo_habilitado) return '';
        if (cliente.porcentaje_uso >= 100) return 'table-danger';
        if (cliente.porcentaje_uso >= 95) return 'table-danger';
        if (cliente.porcentaje_uso >= 80) return 'table-warning';
        return '';
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            fetchClientes(newPage, debouncedSearch);
        }
    };

    const handleEditClick = (cliente) => {
        setSelectedCliente(cliente);
        setEditLimiteGb(cliente.almacenamiento_limite_gb || 0);
        setEditError('');
        setShowEditModal(true);
    };

    const handleEditConfirm = async () => {
        const value = parseFloat(editLimiteGb);
        if (isNaN(value) || value < 0) {
            setEditError('Ingrese un valor válido en GB (0 deshabilita el módulo)');
            return;
        }

        setSaving(true);
        try {
            await axios.put(`/superadmin/archivos-clinicos/${selectedCliente.client_id}/limite`, {
                almacenamiento_limite_gb: value,
            });
            setShowEditModal(false);
            showAlertMessage('success', value > 0 ? 'Cuota actualizada correctamente' : 'Módulo deshabilitado para el cliente');
            fetchClientes(currentPage, debouncedSearch);
        } catch (error) {
            setEditError(error.response?.data?.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleRecalcular = async (cliente) => {
        try {
            await axios.post(`/superadmin/archivos-clinicos/${cliente.client_id}/recalcular`);
            showAlertMessage('success', `Consumo recalculado para ${cliente.nombre}`);
            fetchClientes(currentPage, debouncedSearch);
        } catch (error) {
            showAlertMessage('danger', error.response?.data?.message || 'Error al recalcular');
        }
    };

    if (loading && clientes.length === 0) {
        return (
            <SuperadminLayout>
                <div className="loading-container">
                    <div className="spinner-border text-info" role="status">
                        <span className="sr-only">Cargando...</span>
                    </div>
                </div>
            </SuperadminLayout>
        );
    }

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4><i className="fas fa-images"></i> Archivos clínicos — almacenamiento</h4>
            </div>

            <p className="text-muted small mb-3">
                Cuota por cliente para imágenes y PDF. <strong>0 GB</strong> deshabilita el módulo en atención al paciente.
                Filas en amarillo ≥ 80 % de uso; en rojo ≥ 95 %.
            </p>

            {alert.show && (
                <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
                    {alert.message}
                    <button type="button" className="close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
                        <span>&times;</span>
                    </button>
                </div>
            )}

            <div className="row mb-3">
                <div className="col-md-6">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar por nombre de cliente..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-hover superadmin-table">
                    <thead>
                        <tr>
                            <th>Cliente</th>
                            <th>Límite</th>
                            <th>Consumo</th>
                            <th>% uso</th>
                            <th>Estado</th>
                            <th>Opciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clientes.length > 0 ? clientes.map((cliente) => (
                            <tr key={cliente.client_id} className={getRowClass(cliente)}>
                                <td>{cliente.nombre}</td>
                                <td>{cliente.modulo_habilitado ? formatBytes(cliente.almacenamiento_limite_bytes) : '—'}</td>
                                <td>{cliente.modulo_habilitado ? formatBytes(cliente.almacenamiento_consumo_bytes) : '—'}</td>
                                <td>{cliente.modulo_habilitado ? `${cliente.porcentaje_uso}%` : '—'}</td>
                                <td>
                                    {cliente.modulo_habilitado ? (
                                        <span className="badge badge-success">Habilitado</span>
                                    ) : (
                                        <span className="badge badge-secondary">Deshabilitado</span>
                                    )}
                                </td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-dark mr-1"
                                        onClick={() => handleEditClick(cliente)}
                                        title="Editar cuota"
                                    >
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-info"
                                        onClick={() => handleRecalcular(cliente)}
                                        title="Recalcular consumo desde archivos"
                                    >
                                        <i className="fas fa-sync-alt"></i>
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="6" className="text-center">No se encontraron clientes</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <nav>
                    <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Anterior</button>
                        </li>
                        <li className="page-item active">
                            <span className="page-link">{currentPage} / {totalPages}</span>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Siguiente</button>
                        </li>
                    </ul>
                </nav>
            )}

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Cuota de almacenamiento</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-muted mb-2">
                        Cliente: <strong>{selectedCliente?.nombre}</strong>
                    </p>
                    <p className="small text-muted">
                        Consumo actual: {formatBytes(selectedCliente?.almacenamiento_consumo_bytes || 0)}
                    </p>
                    <div className="form-group">
                        <label htmlFor="limiteGb">Límite (GB)</label>
                        <input
                            id="limiteGb"
                            type="number"
                            className={`form-control ${editError ? 'is-invalid' : ''}`}
                            min="0"
                            step="0.5"
                            value={editLimiteGb}
                            onChange={(e) => setEditLimiteGb(e.target.value)}
                        />
                        <small className="form-text text-muted">
                            Ejemplos: 1, 5, 10. Use <strong>0</strong> para deshabilitar el módulo.
                        </small>
                        {editError && <div className="invalid-feedback d-block">{editError}</div>}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancelar</Button>
                    <Button variant="primary" onClick={handleEditConfirm} disabled={saving}>
                        {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </SuperadminLayout>
    );
};

export default ArchivosClinicosControl;
